import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { useTheme } from '../../contexts/ThemeContext'

const VAT_DATA = [
  {
    country: 'Germany',
    code: 'DEU',
    vatRate: '19%',
    reducedRate: '7%',
    threshold: '€10,000',
    registrationTime: '4-6 weeks',
    requirements: [
      'VAT registration via BZSt (Federal Central Tax Office)',
      'German EORI number for customs',
      'Monthly/quarterly VAT returns',
      'Annual VAT declaration',
    ],
    penalties: 'Late filing: up to 10% of unpaid VAT',
  },
  {
    country: 'France',
    code: 'FRA',
    vatRate: '20%',
    reducedRate: '5.5% / 10%',
    threshold: '€10,000',
    registrationTime: '2-4 weeks',
    requirements: [
      'Registration with French tax authorities (SIE)',
      'French EORI number',
      'Monthly VAT returns (CA3)',
      'Annual turnover declaration',
    ],
    penalties: 'Late filing: 10% penalty + 0.4% interest/month',
  },
  {
    country: 'Netherlands',
    code: 'NLD',
    vatRate: '21%',
    reducedRate: '9%',
    threshold: '€10,000',
    registrationTime: '1-2 weeks',
    requirements: [
      'Registration via Belastingdienst',
      'Dutch VAT number',
      'Quarterly VAT returns',
      'Intrastat declarations if applicable',
    ],
    penalties: 'Late payment: 3% + €67 minimum',
  },
  {
    country: 'Spain',
    code: 'ESP',
    vatRate: '21%',
    reducedRate: '10% / 4%',
    threshold: '€10,000',
    registrationTime: '2-3 weeks',
    requirements: [
      'NIE number required',
      'Registration with AEAT',
      'SII real-time reporting',
      'Modelo 303 quarterly returns',
    ],
    penalties: 'Late filing: 1-20% surcharge based on delay',
  },
  {
    country: 'Italy',
    code: 'ITA',
    vatRate: '22%',
    reducedRate: '10% / 5% / 4%',
    threshold: '€10,000',
    registrationTime: '4-6 weeks',
    requirements: [
      'Italian fiscal representative required',
      'SDI electronic invoicing',
      'Monthly/quarterly LIPE declarations',
      'Annual VAT declaration',
    ],
    penalties: 'Late filing: €250-2,000 per return',
  },
  {
    country: 'Poland',
    code: 'POL',
    vatRate: '23%',
    reducedRate: '8% / 5%',
    threshold: '€10,000',
    registrationTime: '2-3 weeks',
    requirements: [
      'NIP tax identification number',
      'JPK_VAT files (monthly)',
      'SAF-T compliance',
      'Quarterly VAT returns',
    ],
    penalties: 'Late payment: 8% annual interest',
  },
]

export const VATCompliancePage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/compliance')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primary,
              cursor: 'pointer',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            &larr; Back to Compliance Center
          </button>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            VAT & Tax Compliance
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            VAT registration requirements, rates, and filing obligations by market
          </p>
        </div>

        {/* Quick Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '32px' }}>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: 700, color: colors.primary }}>19-23%</div>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>Standard VAT Range</div>
          </Card>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#50C878' }}>€10,000</div>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>EU Distance Selling Threshold</div>
          </Card>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#FF6B35' }}>OSS</div>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>One-Stop Shop Available</div>
          </Card>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: 700, color: '#7B68EE' }}>1-6</div>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>Weeks Registration</div>
          </Card>
        </div>

        {/* OSS Information */}
        <Card style={{ marginBottom: '32px', borderLeft: `4px solid ${colors.primary}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <span style={{ fontSize: '32px' }}>&#128161;</span>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.textPrimary }}>
                EU One-Stop Shop (OSS)
              </h3>
              <p style={{ color: colors.textSecondary, lineHeight: 1.6 }}>
                Since July 2021, UK businesses can register for the Import One-Stop Shop (IOSS) to simplify VAT
                reporting for B2C sales across all EU member states. This allows you to report and pay VAT for all
                EU sales through a single registration, avoiding the need for multiple country registrations.
              </p>
            </div>
          </div>
        </Card>

        {/* Country VAT Details */}
        <h2 style={{ fontSize: '20px', marginBottom: '20px', color: colors.textPrimary }}>
          VAT Requirements by Country
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {VAT_DATA.map((vat) => (
            <Card key={vat.code}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <h3 style={{ fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
                      {vat.country}
                    </h3>
                    <span style={{
                      padding: '4px 12px',
                      backgroundColor: colors.primary + '15',
                      borderRadius: '16px',
                      fontSize: '13px',
                      color: colors.primary,
                      fontWeight: 600,
                    }}>
                      {vat.vatRate} Standard Rate
                    </span>
                    <span style={{
                      padding: '4px 12px',
                      backgroundColor: '#50C87815',
                      borderRadius: '16px',
                      fontSize: '13px',
                      color: '#50C878',
                    }}>
                      {vat.reducedRate} Reduced
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: colors.textPrimary }}>
                        Registration Requirements
                      </h4>
                      <ul style={{ margin: 0, paddingLeft: '16px', color: colors.textSecondary, fontSize: '14px', lineHeight: 1.8 }}>
                        {vat.requirements.map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div style={{ marginBottom: '16px' }}>
                        <span style={{ fontSize: '13px', color: colors.textSecondary }}>Distance Selling Threshold</span>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{vat.threshold}</div>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <span style={{ fontSize: '13px', color: colors.textSecondary }}>Registration Time</span>
                        <div style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{vat.registrationTime}</div>
                      </div>
                      <div>
                        <span style={{ fontSize: '13px', color: colors.textSecondary }}>Penalties</span>
                        <div style={{ fontSize: '14px', color: '#E53E3E' }}>{vat.penalties}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/compliance/${vat.country}`)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Full Details
                </button>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  )
}
